import { Button, FormError, GenericError, Header, LoadingSpinner, Text } from '@psyomics/ui-components';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReferralRequestStatus } from './generated/referral-request';
import { useReferralRequest } from './hooks/use-referral-request';
import { useSubmitReferralRequest } from './hooks/use-submit-referral-request';

type ReferralRequestSubmitProps = { readonly id: string };

const ReferralRequestSubmit = ({ id }: ReferralRequestSubmitProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { referralRequest: draftReferralRequest, fetching: fetchingDraft } = useReferralRequest(
    id,
    ReferralRequestStatus.Draft
  );
  const { errorMessage, fetching: submitting, submitted, submitReferralRequest } = useSubmitReferralRequest();

  useEffect(() => {
    if (submitted) navigate('/');
  }, [submitted, navigate]);

  if (fetchingDraft) return <LoadingSpinner />;
  if (!draftReferralRequest) return <GenericError titleKey="common.errors.notFound" />;

  const {
    readinessChecks: { isReady, errors: readinessErrors },
  } = draftReferralRequest;

  const readinessErrorMessages = readinessErrors.map((key) => t(`page.professionalReferral.submit.errors.${key}`));

  return (
    <div>
      <Header level="h1">{t('page.professionalReferral.submit.title')}</Header>
      {isReady && <Text className="mt-4">{t('page.professionalReferral.submit.cautionText')}</Text>}
      {errorMessage && <FormError className="mt-4" error={t('page.professionalReferral.submit.notReadyError')} />}
      {!errorMessage && !isReady && (
        <FormError
          className="mt-4"
          error={
            readinessErrorMessages.length > 0
              ? readinessErrorMessages
              : t('page.professionalReferral.submit.notReadyError')
          }
        />
      )}
      <div className="mt-4 flex flex-row align-top gap-2">
        <Button appearance="secondary" onClick={() => navigate('/')}>
          {t('common.buttons.cancel')}
        </Button>
        {isReady && (
          <Button loading={submitting} onClick={() => submitReferralRequest(id)}>
            {t('common.buttons.send')}
          </Button>
        )}
        {!isReady && (
          <Button loading={submitting} onClick={() => navigate(`/referral-request/${id}/edit`)}>
            {t('common.buttons.edit')}
          </Button>
        )}
      </div>
    </div>
  );
};

export { ReferralRequestSubmit };
